import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { searchClassForAnalytics, searchStudentForAnalytics } from '../../server/server'
import { ReactComponent as SearchBlue } from '../../assets/svg/SearchBlue.svg'
import { ReactComponent as UserCardId } from '../../assets/svg/UserCardId.svg'
import SearchModal from './student/SearchModal'
import InfoButton from '../common/InfoButton'
import { Link } from 'react-router-dom/cjs/react-router-dom.min'
import { useUser } from '../../auth'
import Loading from '../Loading'

function AdvancedAnalyticsSearchWidget() {
    const { t, locale } = useTranslation()
    const [searchText, setSearchText] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const [showSearchResults, setShowSearchResults] = useState(false)
    const [loading, setLoading] = useState(false)
    const [initLoading, setInitLoading] = useState(true)
    const [results, setResults] = useState([])
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [total, setTotal] = useState(0)
    const [selectedButton, setSelectedButton] = useState('students') // students | classes
    const studentsSelected = selectedButton === 'students'

    useEffect(() => {
        if (searchText || isFocused) {
            const delayDebounceFn = setTimeout(() => {
                fetchResults(searchText, 0)
            }, 400)
            return () => clearTimeout(delayDebounceFn)
        } else {
            // setResults([])
            // setTotal(0)
            // setTimeout(() => {
            //     // setShowSearchResults(false)
            // }, 2000)
        }
    }, [searchText, isFocused])

    const loadMore = () => {
        if (!loadingMore && !loading) {
            setLoadingMore(true)
            fetchResults(searchText, results?.length)
        }
    }

    const fetchResults = async (searchTerm, resultsNum) => {
        setLoading(true)
        const data = {
            searchText: searchTerm.toLowerCase(),
            loadedData: resultsNum,
        }
        const callFunction = studentsSelected ? searchStudentForAnalytics : searchClassForAnalytics
        const resp = await callFunction(data)
        if (resp.status === 200) {
            const array = resp?.data?.results
            const size = resp?.data?.total_count?.count
            if (array) {
                if (resultsNum === 0) {
                    setResults(array.splice(0, 4))
                } else {
                    setResults((prev) => [...prev, ...array])
                }
                setTotal(size)
                setHasMore(size > results?.length)
            } else {
                setResults([])
                setTotal(0)
            }
            setShowSearchResults(true)
        } else {
            setResults([])
            setTotal(0)
        }
        setLoading(false)
        setLoadingMore(false)
    }

    const changeText = (e) => {
        setSearchText(e.target.value)
        setLoading(true)
        // setShowSearchResults(true)
        if (initLoading) {
            setInitLoading(false)
        }
    }

    useEffect(() => {
        setShowSearchResults(false)
        setSearchText('')
    }, [selectedButton])

    return (
        <div className=" rounded-[10px] bg-[white] px-[35px] py-[25px] ">
            <div className="flex flex-row items-center justify-between gap-[10px]">
                <p className=" text-[20px] font-bold text-[#413F5E]">{t('Search All Analytics')}</p>
                <InfoButton text={t('Use this search to explore student, class, or counsellor analytics and access deeper insights.')} />
            </div>

            <SearchOptionButtons selected={selectedButton} setSelected={setSelectedButton} />
            <div
                className={`mt-[16px] flex flex-row items-center justify-center gap-[6px] border border-[#BEBECB] rounded-full h-[44px] px-[20px] bg-white relative transition-all`}
            >
                <input
                    type="text"
                    placeholder={studentsSelected ? t('Search for students') : t('Search for classes')}
                    className="flex-1 transition-all bg-[transparent] placeholder:text-[#9392AD] leading-5 text-[16px] text-deepPurple font-normal "
                    value={searchText}
                    id="advanced-analytics-search-input"
                    onChange={changeText}
                    onFocus={() => {
                        setIsFocused(true)
                    }}
                    onBlur={() => {
                        setIsFocused(false)
                    }}
                />
                {loading ? <Loading primaryColor="#413F5E" /> : <SearchBlue color="#5E639B" />}
            </div>
            {showSearchResults && (
                <div className="flex flex-col gap-[16px] mt-[10px]">
                    <p className="px-[8px] text-[#413F5E] font-medium text-[14px]">
                        {loading ? t('Loading') + '...' : <span>{studentsSelected ? t('We found student(s)', { total }) : t('We found class(es)', { total })}</span>}
                    </p>
                    <div className="flex flex-col gap-[8px]">
                        {results.map((e) => (
                            <Link to={studentsSelected ? `/analytics/student?id=${e.id}` : `/analytics/class?id=${e.id}`}>
                                <div key={e.id} className="h-[40px] bg-[#F8F8FB] rounded-[8px] px-[16px] flex flex-row items-center gap-[16px]">
                                    <p className="text-[16px] text-[#413F5E] font-semibold">{e.label}</p>
                                    <p className="text-[#77768D] font-normal text-[15px]">{e.additionalInformation}</p>
                                    <div className="flex-1" />

                                    <div className="flex flex-row items-center gap-[6px]">
                                        <UserCardId />
                                        <p className="text-[#413F5E] font-semibold text-[12px]">{studentsSelected ? t('Student analytics') : t('Class analytics')}</p>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                    {total > 4 && (
                        <div className="flex flex-row gap-[6px] items-center">
                            <Link to={`analytics/search?search=${searchText}&type=${selectedButton}`}>
                                <button
                                    id="view-more-button"
                                    className="h-[40px] flex flex-row items-center justify-center bg-[#ECEBF8] rounded-full px-[20px] text-[#413F5E] font-bold text-[14px] hover:text-[white] transition-all ease-in-out hover:bg-[#9392AD]"
                                >
                                    {t('View More')}
                                </button>
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}

export default AdvancedAnalyticsSearchWidget

export const SearchOptionButtons = ({ selected, setSelected }) => {
    const { t } = useTranslation()
    const { user } = useUser()

    const studentsSelected = selected === 'students'

    // const showCounsellorButton = useMemo(() => {
    //     const isAdmin = user?.role === 'school_admin' || user?.role_coverage?.target_text === 'All students'
    //     return ['5f25b9fc7fc9788dacc0ce7d', '625486a1b964d3df1b2b9163'].includes(user?.institution_id) && isAdmin
    // }, [user])

    return (
        <div className="flex flex-row items-center gap-[20px] mt-[16px]">
            {!user?.allowedToAccessOrganizationAnalytics && <p>{t('Search for')}</p>}
            <div className="flex flex-row gap-[10px] items-center">
                <button
                    className={`h-[30px] rounded-full min-w-[130px] border text-center font-semibold text-[16px] transition-all ${
                        studentsSelected ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                    } `}
                    onClick={() => setSelected('students')}
                >
                    {t('Students')}
                </button>
                <button
                    className={`h-[30px] rounded-full min-w-[130px] border text-center font-semibold text-[16px] transition-all ${
                        !studentsSelected ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                    } `}
                    onClick={() => setSelected('classes')}
                    id="classes-analytics-button"
                >
                    {t('Classes')}
                </button>

                <Link
                    to={`/analytics/counsellor`}
                    className={`flex items-center justify-center h-[30px] rounded-full min-w-[130px] border text-center font-semibold text-[16px] transition-all px-[15px] pb-0 mb-0 ${
                        false ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                    } `}
                    id="counsellor-analytics-button"
                >
                    <p>{t('Counsellor')}</p>
                </Link>
                {user?.allowedToAccessOrganizationAnalytics && (
                    <Link
                        to={`/analytics/organization`}
                        className={`flex items-center justify-center h-[30px] rounded-full min-w-[130px] border text-center font-semibold text-[16px] transition-all px-[15px] pb-0 mb-0 ${
                            false ? 'bg-deepPurple border-deepPurple text-white' : 'border-[#BEBECE] text-[#77768D] hover:border-deepPurple hover:text-deepPurple'
                        } `}
                        id="organization-analytics-button"
                    >
                        <p>{t('Organization')}</p>
                    </Link>
                )}
            </div>
        </div>
    )
}
